import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TAG_TOOLTIP_DATA } from '@simlab/simlab-facility-management/common';
import { AssetFacade } from '@simOn/asset';
import { isAutoDetectPreviewAsset } from '@simOn/asset/auto-detect-asset/models';
import { TAsset } from '@simOn/asset/modifying/models';
import { AttachmentThumbnailService } from '@simOn/common/media';
import { Attachment } from '@simOn/common/models';
import { IScanControllerToken, MarkerOpenIntentService, MarkerOpenSection } from '@simOn/common/scan';
import { SimAttachmentTileComponent } from '@simOn/ui/sim-attachment-tile';
import { SimSpinnerComponent } from '@simOn/ui/sim-spinner';
import { map, of, switchMap } from 'rxjs';

/**
 * Hover card for an asset marker on the 3D scan. Rendered by `simlab-tag-tooltip-host`
 * from `ComponentConfiguration.tooltip = { component: AssetTooltipComponent }`;
 * the asset id comes from the marker `userData` via `TAG_TOOLTIP_DATA`.
 * "Show more" replays the marker click through the scan controller, which opens the asset side sheet.
 */
@Component({
  selector: 'asset-tooltip',
  imports: [SimAttachmentTileComponent, SimSpinnerComponent],
  templateUrl: './asset-tooltip.component.html',
  styleUrl: './asset-tooltip.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetTooltipComponent {
  private readonly _data = inject(TAG_TOOLTIP_DATA);
  private readonly _assetFacade = inject(AssetFacade);
  private readonly _thumbnails = inject(AttachmentThumbnailService);
  private readonly _scanController = inject(IScanControllerToken, { optional: true });
  private readonly _openIntent = inject(MarkerOpenIntentService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _firstImage = computed<Attachment | undefined>(() => this.asset()?.attachedImages?.[0]);

  private readonly _imageKey = computed(() => {
    const image = this._firstImage();
    return image ? `${image.id}|${image.blobPath ?? ''}|${image.urlWithToken ?? ''}` : '';
  });
  /** Url of the first attached image (small size when available), keyed by the image it belongs to. */
  private readonly _imageUrlState = toSignal(
    toObservable(this._imageKey).pipe(
      switchMap((key) => {
        const image = untracked(() => this._firstImage());
        if (!image) return of({ key, url: '' });
        return this._thumbnails
          .thumbnailUrl$(image)
          .pipe(map((url) => ({ key, url: url || this._thumbnailFallback(untracked(() => this.asset())) })));
      })
    ),
    { initialValue: { key: '\u0000', url: '' } }
  );

  /** Set when the store entry had no attachment data and the detail request was dispatched. */
  private readonly _detailRequestedFor = signal<unknown>(null);

  protected readonly ready = computed(() => {
    const asset = this.asset();
    const requestedFor = this._detailRequestedFor();
    // after `getAsset` the reducer upserts a new entity reference; until then attachments are unknown
    const detailsReady = requestedFor === null || (!!asset && asset !== requestedFor);
    const imageReady = this._imageUrlState().key === this._imageKey();
    return !!asset && detailsReady && imageReady;
  });

  protected readonly assetId = (this._data.userData?.['assetId'] as string | undefined) ?? this._data.id;
  /** Asset generated by auto-detection and not confirmed yet: exists only in the store, cannot be opened. */
  protected readonly isPreview = isAutoDetectPreviewAsset(this.assetId);
  protected readonly asset = toSignal(this.assetId ? this._assetFacade.selectAssetById$(this.assetId) : of(undefined), {
    initialValue: undefined
  });

  protected readonly imagesCount = computed(() => this.asset()?.attachedImages?.length ?? 0);
  protected readonly videosCount = computed(() => this.asset()?.hyperlinks?.length ?? 0);
  protected readonly documentsCount = computed(() => this.asset()?.attachedDocuments?.length ?? 0);
  protected readonly hasAttachments = computed(
    () => this.imagesCount() > 0 || this.videosCount() > 0 || this.documentsCount() > 0
  );
  protected readonly canShowMore = !!this._scanController && !this.isPreview;

  protected readonly imageUrl = computed(() => this._imageUrlState().url);

  constructor() {
    this._destroyRef.onDestroy(() => this._thumbnails.revoke(this.imageUrl()));
    this._ensureAssetDetails();
  }

  /**
   * `GetAssets` (the list) returns entities without attachments, so counts and tiles are
   * empty until the detail endpoint upserts the full asset. Request it when the store
   * entry has no attachment data yet; the selector re-emits after the upsert.
   */
  private _ensureAssetDetails(): void {
    const asset = this.asset();
    if (!this.assetId || this.isPreview) return;
    const hasAttachmentData =
      !!asset &&
      ((asset.attachedImages?.length ?? 0) > 0 ||
        (asset.attachedDocuments?.length ?? 0) > 0 ||
        (asset.hyperlinks?.length ?? 0) > 0);
    if (!hasAttachmentData) {
      this._detailRequestedFor.set(asset ?? undefined);
      this._assetFacade.getAsset(this.assetId);
    }
  }

  /** Opens the asset side sheet; with `section` it scrolls to that attachments section. */
  showMore(section: MarkerOpenSection | null = null): void {
    const asset = this.asset();
    if (!this._scanController || !asset) return;
    this._openIntent.set(section);
    this._scanController.openMarker({
      id: this._data.id,
      userData: this._data.userData ?? { type: 'assets', assetId: asset.id }
    });
  }

  private _thumbnailFallback(asset: TAsset | undefined): string {
    return asset?.thumbnailUrlWithSasToken ?? '';
  }
}
